import { useEffect, useState } from "react";
import { LogOut, LayoutDashboard, Users, KeyRound, LifeBuoy, FlaskConical, Search, FileKey2, RotateCcw, ShieldCheck, QrCode, Smartphone, Settings, CreditCard, CalendarCheck, BarChart3, UserRound, Bell } from "lucide-react";
import { SRMark } from "./SRMark";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { restartDemoTour } from "../lib/demoSession";
import { toast } from "sonner";
import { EditableLayoutRow } from "./EditableLayoutRow";
import { supabase } from "../lib/supabaseClient";
import { authedFetch } from "../lib/api";
import { RecoveryEmailManager } from "./RecoveryEmailManager";

const billingStatusLabel = (account) => {
  if (!account) return "Not active";
  if (account.subscription_status === "TRIAL" && !account.trial_started_at) return "Awaiting trial activation";
  if (account.subscription_status === "FREE_LIFETIME") return "Free for lifetime";
  if (account.subscription_status === "FREE") return "Complimentary access";
  return account.subscription_status || "Not active";
};

export const AppHeader = ({ firstName, role = "gp", sampleMode = false, demoLinkMode = false, demoNav, demoScope = "sample-demo" }) => {
  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [guideOpen, setGuideOpen] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);
  const [mfaPolicy, setMfaPolicy] = useState({ required: false, methods: [] });
  const [mfaFactors, setMfaFactors] = useState([]);
  const [mfaSetup, setMfaSetup] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [phone, setPhone] = useState("");
  const [mfaVerified, setMfaVerified] = useState(false);
  const [settingsTab, setSettingsTab] = useState("account");
  const [address, setAddress] = useState(() => ({
    line1: user?.user_metadata?.address_line_1 || "",
    line2: user?.user_metadata?.address_line_2 || "",
    suburb: user?.user_metadata?.suburb || "",
    state: user?.user_metadata?.state || "",
    postcode: user?.user_metadata?.postcode || "",
  }));
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [billingAccount, setBillingAccount] = useState(null);
  const [showProminentSample, setShowProminentSample] = useState(false);
  const defaultIdleMinutes = role === "clinician" || role === "clinic" ? 30 : 0;
  const [idleMinutes, setIdleMinutes] = useState(() => {
    const saved = window.localStorage.getItem("sr-idle-minutes-" + role);
    return saved === null ? defaultIdleMinutes : Number(saved);
  });
  useEffect(() => {
    if (sampleMode || !user) return;
    Promise.all([authedFetch("/security/policy"), supabase.auth.mfa.listFactors(), supabase.auth.mfa.getAuthenticatorAssuranceLevel()]).then(async ([response, factorsResult, assuranceResult]) => {
      const policy = response.ok ? await response.json() : { required: false, methods: [] };
      const enrolled = factorsResult.data?.all || [];
      const verifiedFactors = enrolled.filter((factor) => factor.status === "verified");
      const nativeVerified = assuranceResult.data?.currentLevel === "aal2";
      setMfaPolicy(policy);
      setMfaFactors(verifiedFactors);
      setMfaVerified(nativeVerified || Boolean(policy.email_verified));
      if (policy.required && !nativeVerified && !policy.email_verified) { setSettingsTab("security"); setSecurityOpen(true); }
    }).catch(() => {});
  }, [sampleMode, user]);

  useEffect(() => {
    if (sampleMode || !user || (!isFinite(idleMinutes)) || idleMinutes <= 0) return;
    let timer;
    const resetTimer = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(async () => {
        await signOut();
        toast.info("You were signed out after a period of inactivity.");
        navigate("/login", { replace: true });
      }, idleMinutes * 60 * 1000);
    };
    const events = ["pointerdown", "keydown", "scroll", "touchstart"];
    events.forEach((eventName) => window.addEventListener(eventName, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      window.clearTimeout(timer);
      events.forEach((eventName) => window.removeEventListener(eventName, resetTimer));
    };
  }, [idleMinutes, navigate, sampleMode, signOut, user]);

  useEffect(() => {
    if (sampleMode || !user || !["clinician", "clinic"].includes(role)) return;
    supabase.from("accounts").select("*").eq("billing_email", user.email).maybeSingle()
      .then(({ data }) => setBillingAccount(data || null))
      .catch(() => {});
  }, [role, sampleMode, user]);

  useEffect(() => {
    if (sampleMode || !user) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site/launch-status?audience=${encodeURIComponent(role)}`)
      .then((response) => response.json())
      .then((status) => {
        const joinedAfterLaunch = Boolean(status.launched_at && user.created_at && new Date(user.created_at) >= new Date(status.launched_at));
        setShowProminentSample(!status.is_live || (joinedAfterLaunch && !user.user_metadata?.sample_directory_completed));
      }).catch(() => setShowProminentSample(false));
  }, [role, sampleMode, user]);

  const saveAccountSettings = async () => {
    window.localStorage.setItem("sr-idle-minutes-" + role, String(idleMinutes));
    const { error } = await supabase.auth.updateUser({ data: {
      ...user?.user_metadata,
      address_line_1: address.line1.trim(),
      address_line_2: address.line2.trim(),
      suburb: address.suburb.trim(),
      state: address.state.trim(),
      postcode: address.postcode.trim(),
    }});
    if (error) return toast.error(error.message);
    toast.success("Account settings saved.");
  };

  const changePassword = async () => {
    if (newPassword.length < 12) return toast.error("Use at least 12 characters for your new password.");
    if (newPassword !== confirmPassword) return toast.error("The passwords do not match.");
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return toast.error(error.message);
    setNewPassword(""); setConfirmPassword("");
    toast.success("Password changed.");
  };

  const openBillingPortal = async () => {
    const response = await authedFetch("/billing/portal", { method: "POST", body: JSON.stringify({ return_url: window.location.href }) });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.url) return toast.error(data.detail || "Could not open payment settings.");
    window.location.assign(data.url);
  };

  const beginMfaSetup = async () => {
    const existing = await supabase.auth.mfa.listFactors();
    const unfinished = (existing.data?.all || []).filter((factor) => factor.factor_type === "totp" && factor.status !== "verified");
    await Promise.all(unfinished.map((factor) => supabase.auth.mfa.unenroll({ factorId: factor.id }).catch(() => null)));
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: "totp", friendlyName: "SimplyRefer authenticator" });
    if (error) return toast.error(error.message);
    if (!data?.id || !data?.totp?.secret) return toast.error("Authenticator setup could not be started. Please try again or contact support.");
    setMfaSetup({ type: "totp", factorId: data.id, qr: data.totp.qr_code, secret: data.totp.secret });
  };
  const beginPhoneSetup = async () => {
    if (mfaFactors.filter((factor) => factor.factor_type === "phone").length >= 2) return toast.error("You can keep up to two verified mobile numbers.");
    if (!phone.trim()) return toast.error("Enter your mobile number including the country code, for example +61.");
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: "phone", phone: phone.trim(), friendlyName: "SimplyRefer SMS" });
    if (error) return toast.error(error.message);
    const challenge = await supabase.auth.mfa.challenge({ factorId: data.id });
    if (challenge.error) return toast.error(challenge.error.message);
    setMfaSetup({ type: "phone", factorId: data.id, challengeId: challenge.data.id });
    setMfaCode("");
    toast.success("Verification text sent.");
  };

  const challengeExistingFactor = async (factor) => {
    const challenge = await supabase.auth.mfa.challenge({ factorId: factor.id });
    if (challenge.error) return toast.error(challenge.error.message);
    setMfaSetup({ type: "existing", factorId: factor.id, challengeId: challenge.data.id, factorType: factor.factor_type });
    setMfaCode("");
    if (factor.factor_type === "phone") toast.success("Verification text sent.");
  };

  const verifyMfaSetup = async () => {
    const result = ["phone", "existing"].includes(mfaSetup.type)
      ? await supabase.auth.mfa.verify({ factorId: mfaSetup.factorId, challengeId: mfaSetup.challengeId, code: mfaCode })
      : await supabase.auth.mfa.challengeAndVerify({ factorId: mfaSetup.factorId, code: mfaCode });
    if (result.error) return toast.error(result.error.message);
    toast.success("Two-factor authentication is active.");
    const { data } = await supabase.auth.mfa.listFactors();
    setMfaFactors((data?.all || []).filter((factor) => factor.status === "verified"));
    setMfaSetup(null); setMfaCode(""); setPhone(""); setMfaVerified(true); setSecurityOpen(false);
  };
  const resetMfa = async (factorId) => {
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    if (error) return toast.error(error.message);
    toast.success("2FA reset. You can enrol again now.");
    const { data } = await supabase.auth.mfa.listFactors();
    setMfaFactors((data?.all || []).filter((factor) => factor.status === "verified"));
  };

  const isClinician = role === "clinician";
  const isClinic = role === "clinic";
  const homePath = isClinician ? "/clinician" : isClinic ? "/clinic" : "/dashboard";
  const sampleNav = demoNav || {
    dashboard: "/sample-dashboard",
    clinicians: "/sample-directory",
    referrals: "/sample-referrals",
  };

  const handleLogout = async () => {
    if (sampleMode && demoLinkMode) {
      toast.info("This personalised demo does not use a signed-in account. Select Create Free GP Account or close this browser tab.");
      return;
    }
    if (user) await signOut();
    toast.success("Signed out.");
    navigate("/login", { replace: true });
  };

  const restartTour = () => {
    restartDemoTour(demoScope);
    setGuideOpen(false);
    navigate(sampleNav.dashboard);
  };

  const navItem = (to, label, Icon) => {
    const [toPath, toQuery = ""] = to.split("?");
    const active = pathname === toPath && (!toQuery || search === `?${toQuery}`);
    const cls = "inline-flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-200 " +
      (active ? "bg-secondary text-secondary-foreground" : "text-slate-600 hover:bg-slate-100");
    const testId = "nav-" + label.toLowerCase().replace(/\s/g, "-");
    return (
      <Link to={to} data-testid={testId} className={cls}>
        <Icon className="h-4 w-4" />
        {label}
      </Link>
    );
  };

  const brand = (
    <span className="flex items-center gap-2.5">
      <SRMark />
      <span className="font-heading text-lg font-semibold tracking-tight text-slate-900">SimplyRefer</span>
    </span>
  );

  return (
    <>
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex min-w-0 flex-1 items-center gap-3 lg:gap-6">
            {sampleMode ? (
              <button type="button" onClick={() => setGuideOpen(true)} className="rounded-md text-left focus:outline-none focus:ring-2 focus:ring-primary" data-testid="demo-guide-logo" title="Open the demo guide">
                {brand}
              </button>
            ) : (
              <Link to={homePath} className="flex shrink-0 items-center gap-2.5">{brand}</Link>
            )}
            {!sampleMode && (
              <EditableLayoutRow
                scope={isClinician ? "clinician-navigation" : isClinic ? "clinic-navigation" : "gp-navigation"}
                ariaLabel={isClinician ? "Clinician navigation" : isClinic ? "Practice manager navigation" : "GP navigation"}
                className="hidden min-w-0 flex-1 items-center gap-1 overflow-x-auto sm:flex"
                items={isClinician ? [
                  { id: "overview", label: "Overview", node: navItem("/clinician?tab=overview", "Overview", LayoutDashboard) },
                  { id: "availability", label: "Availability", node: navItem("/clinician?tab=availability", "Availability", CalendarCheck) },
                  { id: "find", label: "Find clinician", node: navItem("/clinicians", "Find clinician", Search) },
                  { id: "referrals", label: "Referrals", node: navItem("/clinician?tab=referrals", "Referrals", KeyRound) },
                  { id: "insights", label: "Insights", node: navItem("/clinician?tab=insights", "Insights", BarChart3) },
                  { id: "settings", label: "Settings", node: navItem("/clinician?tab=settings", "Settings", Settings) },
                ] : isClinic ? [
                  { id: "overview", label: "Overview", node: navItem("/clinic?tab=overview", "Overview", LayoutDashboard) },
                  { id: "team", label: "Team", node: navItem("/clinic?tab=team", "Team", Users) },
                  { id: "find", label: "Find clinician", node: navItem("/clinicians", "Find clinician", Search) },
                  { id: "referrals", label: "Referrals", node: navItem("/clinic?tab=referrals", "Referrals", KeyRound) },
                  { id: "insights", label: "Insights", node: navItem("/clinic?tab=insights", "Insights", BarChart3) },
                ] : [
                  { id: "dashboard", label: "Dashboard", node: navItem("/dashboard", "Dashboard", LayoutDashboard) },
                  { id: "clinicians", label: "Clinicians", node: navItem("/clinicians", "Clinicians", Users) },
                  { id: "referrals", label: "SimplyRefer References", node: navItem("/referrals", "SimplyRefer References", KeyRound) },
              { id: "saved-searches", label: "Saved Searches", node: navItem("/saved-searches", "Saved Searches", Bell) },
                ]}
              />
            )}
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-3 sm:gap-4">
            {firstName && <span className="hidden text-sm text-slate-600 md:inline" data-testid="nav-user-name">{firstName}</span>}
            {!sampleMode && user && showProminentSample && (
              <Button type="button" variant={user.user_metadata?.sample_directory_completed ? "ghost" : "default"} size="sm" onClick={() => navigate("/sample-directory")} data-testid="header-sample-directory">
                <FlaskConical className="mr-2 h-4 w-4" /> {(isClinician || isClinic) ? "View Sample Directory" : "Sample directory"}
              </Button>
            )}
            {!sampleMode && (
              <Button type="button" variant={mfaPolicy.required && mfaFactors.length === 0 ? "default" : "ghost"} size="sm" onClick={() => { setSettingsTab(mfaPolicy.required && !mfaVerified ? "security" : "account"); setSecurityOpen(true); }} data-testid="account-settings-button">
                <Settings className="mr-2 h-4 w-4" /> Settings
              </Button>
            )}
            {!sampleMode && (
              <a href="mailto:hello@simplyrefer.com.au" className="hidden items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-primary sm:inline-flex" data-testid="support-link" title="Contact support">
                <LifeBuoy className="h-4 w-4" /> Support
              </a>
            )}
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="mr-2 h-4 w-4" /> Log out
            </Button>
          </div>
        </div>
        {sampleMode && (
          <div className="border-t border-slate-100 bg-slate-50/80" data-testid="demo-primary-navigation">
            <nav className="mx-auto flex max-w-7xl items-center gap-1 overflow-x-auto px-4 py-2 sm:px-6 lg:px-8" aria-label="Demo navigation">
              {navItem(sampleNav.dashboard, "Dashboard", LayoutDashboard)}
              {navItem(sampleNav.clinicians, "Clinicians", Users)}
              {navItem(sampleNav.referrals, "SimplyRefer References", KeyRound)}
              <Button type="button" variant="ghost" size="sm" onClick={() => setGuideOpen(true)} className="ml-auto shrink-0" data-testid="open-demo-guide">
                <LifeBuoy className="mr-2 h-4 w-4" /> Demo guide
              </Button>
            </nav>
          </div>
        )}
      </header>

      {!sampleMode && (
        <Dialog open={securityOpen} onOpenChange={(open) => { if (!open && mfaPolicy.required && !mfaVerified && !mfaPolicy.email_verified) return; setSecurityOpen(open); }}>
          <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto bg-white" data-testid="user-settings-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Settings className="h-5 w-5 text-primary" /> Account settings</DialogTitle>
              <DialogDescription>Manage your account, security, session and billing details.</DialogDescription>
            </DialogHeader>
            <div className="mt-2 flex flex-wrap gap-2 border-b border-slate-200 pb-3">
              <Button size="sm" variant={settingsTab === "account" ? "default" : "ghost"} onClick={() => setSettingsTab("account")}>Account</Button>
              {(isClinician || isClinic) && <Button size="sm" variant={settingsTab === "security" ? "default" : "ghost"} onClick={() => setSettingsTab("security")}>Security &amp; 2FA</Button>}
              {(isClinician || isClinic) && <Button size="sm" variant={settingsTab === "billing" ? "default" : "ghost"} onClick={() => setSettingsTab("billing")}><CreditCard className="mr-2 h-4 w-4" /> Billing</Button>}
            </div>
            {settingsTab === "account" && (
              <div className="mt-4 space-y-5">
                <section className="rounded-md border border-slate-200 p-4">
                  <h3 className="font-semibold text-slate-900">Address</h3>
                  <p className="mt-1 text-sm text-slate-500">Used for your account and professional profile where relevant.</p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <Input className="sm:col-span-2" value={address.line1} onChange={(e) => setAddress((v) => ({ ...v, line1: e.target.value }))} placeholder="Address line 1" />
                    <Input className="sm:col-span-2" value={address.line2} onChange={(e) => setAddress((v) => ({ ...v, line2: e.target.value }))} placeholder="Address line 2 (optional)" />
                    <Input value={address.suburb} onChange={(e) => setAddress((v) => ({ ...v, suburb: e.target.value }))} placeholder="Suburb" />
                    <Input value={address.state} onChange={(e) => setAddress((v) => ({ ...v, state: e.target.value }))} placeholder="State" />
                    <Input value={address.postcode} onChange={(e) => setAddress((v) => ({ ...v, postcode: e.target.value }))} placeholder="Postcode" />
                  </div>
                </section>
                <section className="rounded-md border border-slate-200 p-4">
                  <h3 className="font-semibold text-slate-900">Password</h3>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password (12+ characters)" />
                    <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" />
                  </div>
                  <Button type="button" className="mt-3" onClick={changePassword}>Change password</Button>
                </section>
                <section className="rounded-md border border-slate-200 p-4">
                  <h3 className="font-semibold text-slate-900">Automatic sign-out</h3>
                  <p className="mt-1 text-sm text-slate-500">{role === "gp" ? "GP accounts remain signed in by default. Choose a limit if you prefer." : "Clinician accounts sign out after 30 minutes of inactivity by default."}</p>
                  <select value={idleMinutes} onChange={(e) => setIdleMinutes(Number(e.target.value))} className="mt-3 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm sm:w-72">
                    <option value={0}>Do not automatically sign out</option>
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                    <option value={60}>1 hour</option>
                    <option value={120}>2 hours</option>
                  </select>
                </section>
                <section className="rounded-md border border-slate-200 p-4">
                  <h3 className="font-semibold text-slate-900">Help and demo</h3>
                  <p className="mt-1 text-sm text-slate-500">Reopen the fictional directory and walkthrough at any time.</p>
                  <Button type="button" className="mt-3" variant="outline" onClick={() => { setSecurityOpen(false); navigate("/sample-directory"); }}><FlaskConical className="mr-2 h-4 w-4" /> Open interactive demo</Button>
                </section>
                <Button type="button" onClick={saveAccountSettings}>Save account settings</Button>
              </div>
            )}
            {settingsTab === "billing" && (isClinician || isClinic) && (
              <div className="mt-4 space-y-4">
                <div className="rounded-md border border-slate-200 p-4">
                  <h3 className="font-semibold text-slate-900">Subscription and payments</h3>
                  <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
                    <div><dt className="text-slate-500">Status</dt><dd className="font-medium text-slate-900">{billingStatusLabel(billingAccount)}{billingAccount?.subscription_cancel_at ? " · cancels at period end" : ""}</dd></div>
                    <div><dt className="text-slate-500">Plan</dt><dd className="font-medium text-slate-900">{billingAccount?.subscription_tier || billingAccount?.account_type || "—"}</dd></div>
                    <div><dt className="text-slate-500">Billing cycle</dt><dd className="font-medium text-slate-900">Monthly</dd></div>
                    <div><dt className="text-slate-500">Next payment</dt><dd className="font-medium text-slate-900">{billingAccount?.next_billing_at ? new Date(billingAccount.next_billing_at).toLocaleDateString("en-AU") : billingAccount?.trial_ends_at ? new Date(billingAccount.trial_ends_at).toLocaleDateString("en-AU") : "—"}</dd></div>
                  </dl>
                  <div className="mt-4 flex flex-wrap gap-2"><Button type="button" onClick={() => { setSecurityOpen(false); navigate("/clinic/subscribe"); }}>Choose plan or add card</Button><Button type="button" variant="outline" onClick={openBillingPortal}>Manage billing and invoices</Button></div>
                  <p className="mt-2 text-xs text-slate-500">Stripe securely provides saved payment methods, past invoices and upcoming billing details.</p>
                </div>
              </div>
            )}
            {settingsTab === "security" && (isClinician || isClinic) && (mfaSetup ? (
              <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 p-4">
                {mfaSetup.qr ? <><p className="mb-2 text-sm text-blue-900">Scan this QR code with Microsoft Authenticator, Google Authenticator, or another standard TOTP app.</p><img src={mfaSetup.qr} alt="Authenticator QR code" className="mx-auto h-44 w-44 rounded bg-white p-2" /><p className="mt-2 text-center text-xs font-medium text-slate-600">Manual setup key</p><p className="mt-1 break-all text-center font-mono text-xs text-slate-500">{mfaSetup.secret}</p></> : <p className="text-sm font-medium text-blue-900">Enter the 6-digit code sent to your mobile.</p>}
                <div className="mt-3 flex gap-2"><Input inputMode="numeric" value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" /><Button onClick={verifyMfaSetup} disabled={mfaCode.length !== 6}>Verify</Button></div>
              </div>
            ) : (
              <div className="mt-3 space-y-3">
                {mfaPolicy.email_verified && <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm font-medium text-emerald-800">Email-code verification completed for this session</div>}
                {mfaFactors.map((factor) => <div key={factor.id} className="flex items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm"><span className="font-medium text-emerald-800">{factor.factor_type === "phone" ? "Text-message 2FA" : "Authenticator app"} is enrolled</span><div className="flex gap-2">{mfaPolicy.required && !mfaVerified && <Button size="sm" onClick={() => challengeExistingFactor(factor)}>Verify now</Button>}<Button size="sm" variant="outline" onClick={() => resetMfa(factor.id)}>Reset</Button></div></div>)}
                {mfaPolicy.required && !mfaVerified && !mfaPolicy.email_verified && <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm font-medium text-amber-900">Verify an enrolled factor before continuing.</p>}
                {mfaPolicy.required && <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm font-medium text-amber-900">2FA setup is required for this account.</p>}
                {!mfaFactors.some((factor) => factor.factor_type === "totp") && (!mfaPolicy.required || mfaPolicy.methods?.includes("totp")) && <Button className="w-full" onClick={beginMfaSetup}><QrCode className="mr-2 h-4 w-4" /> Set up Microsoft Authenticator</Button>}
                {mfaPolicy.methods?.includes("phone") && mfaFactors.filter((factor) => factor.factor_type === "phone").length < 2 && <div className="flex gap-2"><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+61 4…" /><Button type="button" variant="outline" onClick={beginPhoneSetup}><Smartphone className="mr-2 h-4 w-4" /> Add mobile number</Button></div>}
                {mfaPolicy.methods?.includes("email") && <RecoveryEmailManager onSessionVerified={() => { setMfaPolicy((current) => ({ ...current, email_verified: true })); setMfaVerified(true); }} />}
                <p className="text-xs text-slate-500">Microsoft Authenticator is the recommended default. You may also keep up to two verified mobile numbers and two verified email addresses as fallbacks.</p>
              </div>
            ))}
          </DialogContent>
        </Dialog>
      )}

      {sampleMode && (
        <Dialog open={guideOpen} onOpenChange={setGuideOpen}>
          <DialogContent className="max-w-md bg-white" data-testid="demo-guide-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><FlaskConical className="h-5 w-5 text-primary" /> SimplyRefer demo guide</DialogTitle>
              <DialogDescription>Use the demo exactly as a GP would, without creating a real referral or changing live information.</DialogDescription>
            </DialogHeader>
            <div className="mt-3 space-y-3">
              <GuideStep icon={Search} number="1" title="Search fictional clinicians" text="Choose a profession, specialty and any helpful filters to narrow the directory." />
              <GuideStep icon={FileKey2} number="2" title="Simply Refer" text="Open a clinician profile and select for referral, create an optional SimplyRefer Reference, or open a prefilled email." />
              <GuideStep icon={KeyRound} number="3" title="Continue your usual process" text="Use your existing referral system or attach your usual referral letter to the prefilled email." />
              <Button type="button" variant="outline" className="w-full" onClick={restartTour} data-testid="restart-demo-tour"><RotateCcw className="mr-2 h-4 w-4" /> Restart guided tour</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

const GuideStep = ({ icon: Icon, number, title, text }) => (
  <div className="flex gap-3 rounded-md border border-slate-200 bg-slate-50 p-3">
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-white">{number}</div>
    <div>
      <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Icon className="h-4 w-4 text-primary" /> {title}</p>
      <p className="mt-0.5 text-xs leading-relaxed text-slate-600">{text}</p>
    </div>
  </div>
);
